import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';
import { useDrag, useDrop } from "react-dnd";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Property } from '../../../../components/properties';

import { EditorContext } from '../../../../index';
import { updateWidgetContent } from 'actions/editor';

import { Form, Button } from "react-bootstrap";

import styles from './filterList.module.scss';

const ManageFilter = ({ id, widget, position, handleViewChange, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const filterContent = _.get(widget, 'content', []);
  const [state, setState] = useState({
    filterAction: false,
    value: {}
  });

  const modalReference = React.useRef();

  const handleOnChangeValue = (nodes, targetid, type, value) => {
    return (nodes || []).map((element) => {
      if (element.uid === targetid) {
        return {
          ...element,
          [type]: value
        }
      }
      return {
        ...element,
        children: handleOnChangeValue(element.children, targetid, type, value)
      };
    });
  };

  return (
    <>
      <Property.Dialog
        width={253}
        ref={modalReference}
        className={styles.menuDialog}
        target={`#${id}`}
        title='Manage Filters'
        onCancel={rest.onCancel}
      >
        <Property.Body>
          <FilterListContainer 
            filterContent={filterContent} 
            widgetId={id} 
            editorSession={editorSession} />
          <div className={styles.actionConatiner}>
            <Button className={styles.addRemoveButton} onClick={()=> handleViewChange('custom-fields')} variant='secondary'>
              Add/Remove Filters
            </Button>
          </div>
        </Property.Body>
      </Property.Dialog>
    </>
  );
}

const FilterType = {
  FIELD: "field",
  OPTION: "option"
};

const FilterListContainer = ({ filterContent, widgetId, editorSession }) => {
  const dispatch = useDispatch();

  const [filters, setFilters] = React.useState([]);

  useEffect(() => {
    if(filterContent) {
      setFilters(filterContent)
    }
  }, [filterContent])

  const moveFilter = (dragIndex, hoverIndex) => {
    const draggedFilter = filters[dragIndex];

    const newFilters = [...filters];
    newFilters.splice(dragIndex, 1);
    newFilters.splice(hoverIndex, 0, draggedFilter);

    dispatch(updateWidgetContent(editorSession, widgetId, newFilters));
  };

  const moveOption = (dragIndex, hoverIndex, filter) => {
    var options = filter.options;
    const draggedOption = options[dragIndex];

    const newOptions = [...options];
    newOptions.splice(dragIndex, 1);
    newOptions.splice(hoverIndex, 0, draggedOption);

    var newFilters = filters.map(item => {
      if (item.id === filter.id) {
        return {
          ...item,
          options: newOptions
        }
      } 

      return item;
    })

    dispatch(updateWidgetContent(editorSession, widgetId, newFilters));
  };

  const updateFilter = (filter) => {
    var updatedFilters = filters.map(item => {
      if (item.id === filter.id) {
        return filter;
      } 

      return item;
    });

    dispatch(updateWidgetContent(editorSession, widgetId, updatedFilters));
  }

  return (
      <div className={styles.listContainer}>
        {(filters || []).map((filter, index) => (
          <FilterView
            key={filter.id}
            id={filter.id}
            filter={filter}
            filters={filters}
            index={index}
            moveFilter={moveFilter}
            moveOption={moveOption}
            updateFilter={updateFilter}
            editorSession={editorSession}
            widgetId={widgetId}
          />
        ))}
      </div>
  );
};

const FilterView = ({ id, filter, filters, index, moveFilter, moveOption, updateFilter, editorSession, widgetId }) => {
  const ref = React.useRef(null);
  const dispatch = useDispatch();

  const [expand, setExpand] = useState(false);
  const [sortDirection, setSortDirection] = useState('');
  const [options, setOptions] = React.useState([]);
  const customFields = useSelector((state) => _.get(state, "fields.items") || []);

  useEffect(() => {
    if (filter.options) {
      processOptions();
    }
  }, [filter && filter.options]);

  var processOptions = () => {
    var options = [...filter.options];
    var customField = customFields.find(item => item.id === filter.id);
    var fieldOptions = _.get(customField, 'options', []);

    var hiddenOptons = fieldOptions
          .filter(item => !options.some(o => o.id === item.id))
          .map(item => {
            return {
              ...item,
              hidden: true
            }
          });

    setOptions([...options, ...hiddenOptons]);
  }

  const [{ handlerId }, drop] = useDrop({
    accept: FilterType.FIELD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveFilter(dragIndex, hoverIndex);

      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: FilterType.FIELD,
    item: { type: FilterType.FIELD, id, index }, // Added the type property
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const handleOrderChange = () => {
    const newDirection = sortDirection === '' ? 'ASC' : sortDirection === 'ASC' ? 'DESC' : 'ASC';
    
    setSortDirection(newDirection);
  
    if (options.length === 0) return;
  
    let sortedOptions = [...options];
    
    sortedOptions.sort((a, b) =>
      newDirection === 'ASC'
        ? a.value.localeCompare(b.value, undefined, { numeric: true, caseFirst: 'upper' })
        : b.value.localeCompare(a.value, undefined, { numeric: true, caseFirst: 'upper' })
    );
  
    setOptions(sortedOptions);
  
    const formattedOptions = sortedOptions
      .filter(option => !option.hidden)
      .map(option => {
        const { hidden, ...formatOption } = option;
        return formatOption;
      });
  
    const newFilters = filters?.map(item => {
      if (item.id === filter?.id) {
        return {
          ...item,
          options: formattedOptions
        };
      }
      return item;
    });
  
    if (newFilters?.length > 0) {
      dispatch(updateWidgetContent(editorSession, widgetId, newFilters));
    }
  };
  
  return (
    <div ref={ref} style={{ opacity }} data-handler-id={handlerId} className={cx(styles.menuWrapper)}>
      <div ref={preview} className={cx(styles.dropdownItem)}>
        <Handle />

        <span className={styles.name}>
          <i className={cx(styles.icon, 'cleverstory-page')} />
          <TruncatedTextWithTooltip content={filter.name} maxWidth={150} placement='top'/>
        </span>
        <Button
          variant="info"
          onClick={() => setExpand(!expand)}
          className={cx(styles.dropDownButton, {
            [styles.open]: expand,
            [styles.closed]: !expand,
          })}
        >
          <i className={'cleverstory-drop-down'} />
        </Button>
      </div>

      {expand && (
        <>
          <div className={cx(styles.sortContainer)}>
            <SortOrderRender handleOrderChange={handleOrderChange} order={sortDirection} />
          </div>
          <FilterOptions
            filter={filter}
            filters={filters}
            moveOption={moveOption}
            updateFilter={updateFilter}
            sortDirection={sortDirection}
            widgetId={widgetId} 
            editorSession={editorSession}
            options={options}
          />
        </>
      )}
    </div>
  );
};

const FilterOptions = ({ filter, filters, moveOption, updateFilter, sortDirection, widgetId, editorSession, options }) => {

  const handleMoveOption = (dragIndex, hoverIndex) => {
    moveOption(dragIndex, hoverIndex, filter);
  }

  const toggleOptionVisibility = (option) => {
    var updatedOptions = [...options];
    updatedOptions = (filter.options || []).filter(item => item.id !== option.id);

    if(option.hidden) {
      delete option.hidden;
      updatedOptions = [...updatedOptions, option];
    }

    var updatedFilter = {
      ...filter,
      options: updatedOptions
    }

    updateFilter(updatedFilter);
  }

  return (
      <div className={cx(styles.listContainer, styles.optionsContainer)} >
        {(options || []).map((option, index) => (
          <FilterOptionView
            key={option.id}
            id={option.id}
            option={option}
            index={index}
            moveOption={handleMoveOption}
            onToggleVisibility={toggleOptionVisibility}
          />
        ))}
      </div>
  );
};

const SortOrderRender = ({ handleOrderChange, order }) => {
  return (
    <div className={styles.sortActionContainer}>
      <div className={styles.conditions}>
        <span className={styles.label}>
          <span>Sort by: </span>
          <img
            onClick={() => handleOrderChange()}
            className={cx(styles.sortIcon, order === "ASC" ? styles.ascending : styles.descending)}                
            alt="sortIcon"
            src={order === '' ? '/images/sortInitial.svg' : '/images/sortIcon.svg'}
          />
          <span>Name</span>
        </span>
      </div>
    </div>
  );
};

const FilterOptionView = ({ id, option, index, moveOption, onToggleVisibility }) => {
  const ref = React.useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: FilterType.OPTION,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveOption(dragIndex, hoverIndex);

      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: FilterType.OPTION,
    item: { type: FilterType.OPTION, id, index }, // Added the type property
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: !option.hidden
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div ref={ref} 
      onDragStart={(event) => {
        if(option.hidden) {
          event.preventDefault()
        }
      }} // Prevent default drag behavior on the parent element
      style={{ opacity }} 
      data-handler-id={handlerId} 
      className={cx(styles.menuWrapper)}>
      <div ref={preview} className={cx(styles.dropdownItem)}>
        <Handle />

        <TruncatedTextWithTooltip content={option.value} className={styles.name} maxWidth={150} placement='top'/>
        <Button variant='info' className={styles.visibilityButton} onClick={() => onToggleVisibility(option)}>
          {option.hidden ? <i className={cx('cleverstory-hide')} /> : <i className={cx('cleverstory-view')} />}
        </Button>
      </div>
    </div>
  );
};

const Handle = React.forwardRef((props, ref) => {
  return (
    <div className={styles.handle} {...props} ref={ref}>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
    </div>
  );
});

export default ManageFilter;